import React, { useState } from "react";
import { Alert, Dialog, DialogContent } from "@mui/material";
import { EmailCallout } from "../../../components/VendorEmailDialog";
import {
  CollectionContext,
  OrganizationDataRecipientDto,
} from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import { Button } from "@truevault/aquilo";
import validator from "validator";
import { Disableable } from "../../../components/Disableable";
import { Api } from "../../../../common/service/Api";
import { UpdateVendorArgs } from "../components/workflows/create-data-recipient/ClassificationForDataRecipientPage";

import { getDefaultEmailBodyForVendor } from "./vendorDocumentationEmailUtils";
import {
  canSendPrivacyEmail,
  PRIVACY_EMAIL_SETUP_REQUIRED_TOOLTIP,
} from "../../../util/privacyEmailSetup";

export const DEFAULT_VENDOR_DOCUMENTATION_EMAIL_SUBJECT = "Privacy Documentation Inquiry";

export function EmailVendorDialog({
  org,
  vendor,
  collectionContext = "CONSUMER",
  updateVendor,
  isOpen,
  closeModal,
  zIndex,
}: {
  org: OrganizationDto;
  vendor: OrganizationDataRecipientDto;
  collectionContext?: CollectionContext | CollectionContext[];
  updateVendor: (v: UpdateVendorArgs) => Promise<void>;
  isOpen: boolean;
  closeModal: () => void;
  zIndex?: number;
}) {
  const [emailBody, setEmailBody] = useState(
    getDefaultEmailBodyForVendor(org, vendor, collectionContext),
  );
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState(false);

  let vendorEmail = vendor.email || vendor.defaultEmail;
  const canSendEmail = canSendPrivacyEmail(org);
  const isSendDisabled =
    emailLoading || !vendorEmail || !validator.isEmail(vendorEmail) || !canSendEmail;

  const dispatchEmail = async (emailContent: string, subject?: string) => {
    setEmailError(false);
    setEmailLoading(true);

    try {
      const res = await Api.organizationVendor.requestVendorServiceProviderInfo(
        org.id,
        [vendor.vendorId],
        [vendorEmail],
        emailContent,
        subject,
      );
      if (res?.ok === true) {
        const currentDate = new Date().toISOString();

        await updateVendor({
          vendorId: vendor.vendorId,
          vendorSettings: {
            ...vendor,
            serviceProviderInfoRequestSentDate: currentDate,
          },
        });

        closeModal();
      } else {
        setEmailError(true);
      }
    } catch (e) {
      setEmailError(true);
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => closeModal()}
      maxWidth="md"
      fullWidth
      aria-labelledby="email-dialog-title-us"
      aria-describedby="email-dialog-description-us"
      sx={zIndex != null ? { zIndex } : undefined}
    >
      <DialogContent>
        <div className="tw-boundary">
          <div className="mb-[16px] text-[18px] font-medium text-neutral-900">
            Review and send the email below. Our template addresses all missing documentation
            requirements for this vendor. You can remove and edit text before sending.
          </div>
        </div>

        <EmailCallout
          vendorEmail={vendorEmail}
          setVendorEmail={() => {}}
          emailSubject={DEFAULT_VENDOR_DOCUMENTATION_EMAIL_SUBJECT}
          emailBody={emailBody}
          setEmailBody={setEmailBody}
          emailLoading={emailLoading}
          emailSent={false}
          org={org}
        />

        {emailError && (
          <Alert className="mt-md mb-md" severity="error">
            There was an error sending the email.
          </Alert>
        )}

        <div className="tw-boundary">
          <div className="flex items-center justify-end gap-[12px]">
            <Button color="tertiary" onClick={() => closeModal()}>
              Cancel
            </Button>
            <Disableable
              disabled={!canSendEmail}
              disabledTooltip={PRIVACY_EMAIL_SETUP_REQUIRED_TOOLTIP}
              tooltipZIndex={zIndex != null ? zIndex + 1 : undefined}
            >
              <Button
                color="primary"
                disabled={isSendDisabled}
                onClick={() => dispatchEmail(emailBody, DEFAULT_VENDOR_DOCUMENTATION_EMAIL_SUBJECT)}
              >
                Send
              </Button>
            </Disableable>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
